import React, { useEffect, useState, useCallback } from 'react';
import { Switch } from '../ui/switch';
import { PhoneCall, MailCheck, CalendarCheck, Loader2, Tag, UserCog, PhoneForwarded, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const ROWS = [
  {
    key: 'instant_callback_enabled',
    icon: PhoneCall,
    color: 'text-sky-500',
    title: 'Speed-to-lead instant callback',
    desc: 'AI calls hot inbound leads within seconds (respects quiet hours & DNC).',
  },
  {
    key: 'auto_reply_enabled',
    icon: MailCheck,
    color: 'text-emerald-500',
    title: 'Autonomous email & LinkedIn replies',
    desc: 'LEO drafts replies to positive inbound emails & LinkedIn messages. Never replies to opt-outs/objections.',
  },
  {
    key: 'auto_book_meetings_enabled',
    icon: CalendarCheck,
    color: 'text-violet-500',
    title: 'Auto-book meetings',
    desc: 'When a prospect proposes a time, Brain books it and sends the invite.',
  },
  {
    key: 'reply_callback_enabled',
    icon: PhoneForwarded,
    color: 'text-orange-500',
    title: 'Auto-schedule callbacks from replies',
    desc: 'When a reply asks for a call at a specific time, Brain schedules an AI voice callback then (respects DNC, cooldown & quiet hours).',
  },
];

// Phase 2 + 3 — autonomous inbound controls. Opt-in, default OFF.
export const AutonomyControls = () => {
  const [settings, setSettings] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/brain/automation-settings');
      setSettings(res.data?.settings || {});
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/hr/users');
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setTeamMembers(list.filter((u) => u.is_active !== false));
      } catch (e) { /* non-blocking */ }
    })();
  }, []);

  const setOpsAssignee = async (value) => {
    setSavingKey('ops_team_assignee');
    try {
      await api.put('/api/brain/automation-settings', { ops_team_assignee: value });
      setSettings((s) => ({ ...s, ops_team_assignee: value || null }));
      toast.success(value ? 'Operations assignee set' : 'Operations assignee cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update assignee');
      load();
    } finally { setSavingKey(null); }
  };

  const toggle = async (key, value) => {
    setSavingKey(key);
    try {
      await api.put('/api/brain/automation-settings', { [key]: value });
      setSettings((s) => ({ ...s, [key]: value }));
      toast.success(`${ROWS.find((r) => r.key === key).title} ${value ? 'ON' : 'OFF'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update setting');
      load();
    } finally { setSavingKey(null); }
  };

  const setLinkedinAutonomy = async (mode) => {
    if ((settings.linkedin_autonomy || 'semi_auto') === mode) return;
    setSavingKey('linkedin_autonomy');
    try {
      await api.put('/api/brain/automation-settings', { linkedin_autonomy: mode });
      setSettings((s) => ({ ...s, linkedin_autonomy: mode }));
      toast.success(mode === 'full_auto' ? 'LinkedIn replies: auto-send' : 'LinkedIn replies: draft for approval');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update LinkedIn autonomy');
      load();
    } finally { setSavingKey(null); }
  };

  const setPricingPolicy = async (policy) => {    if (settings.pricing_reply_policy === policy) return;
    setSavingKey('pricing_reply_policy');
    try {
      await api.put('/api/brain/automation-settings', { pricing_reply_policy: policy });
      setSettings((s) => ({ ...s, pricing_reply_policy: policy }));
      toast.success(policy === 'auto_answer' ? 'Pricing: auto-answer from KB' : 'Pricing: escalate to a human');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update pricing policy');
      load();
    } finally { setSavingKey(null); }
  };

  if (!settings) return null;
  const pricingPolicy = settings.pricing_reply_policy || 'escalate';
  const liAutonomy = settings.linkedin_autonomy || 'semi_auto';
  const liEnabled = !!settings.auto_reply_enabled;

  return (
    <div data-testid="autonomy-controls-card" className="rounded-xl border border-border/50 bg-card/40 divide-y divide-border/40">
      {ROWS.map(({ key, icon: Icon, color, title, desc }) => (
        <React.Fragment key={key}>
          <div className="flex items-center justify-between gap-4 px-4 py-2.5" data-testid={`autonomy-row-${key}`}>
            <div className="flex items-start gap-3">
              <Icon className={`h-4 w-4 mt-0.5 ${color}`} />
              <div>
                <p className="text-sm font-medium text-foreground">{title}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {savingKey === key ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
              <Switch
                checked={!!settings[key]}
                onCheckedChange={(v) => toggle(key, v)}
                data-testid={`autonomy-switch-${key}`}
              />
            </div>
          </div>

          {/* Nested sub-option of the reply toggle — LinkedIn draft-vs-send.
              Only meaningful when autonomous replies are ON, so it's disabled/greyed otherwise. */}
          {key === 'auto_reply_enabled' && (
            <div
              className={`flex items-center justify-between gap-4 pl-12 pr-4 py-2.5 bg-muted/15 border-l-2 border-sky-500/30 transition-opacity ${liEnabled ? '' : 'opacity-50'}`}
              data-testid="autonomy-row-linkedin_autonomy"
            >
              <div className="flex items-start gap-3">
                <Linkedin className="h-4 w-4 mt-0.5 text-[#0A66C2]" />
                <div>
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    ↳ LinkedIn reply delivery
                    {!liEnabled && (
                      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded" data-testid="linkedin-autonomy-disabled-hint">
                        turn replies on
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">When a lead replies on LinkedIn, LEO drafts a reply — choose whether it waits for your approval or sends automatically.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {savingKey === 'linkedin_autonomy' ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
                <div className="inline-flex rounded-lg border border-border/60 overflow-hidden text-xs">
                  <button
                    type="button"
                    disabled={!liEnabled}
                    onClick={() => setLinkedinAutonomy('semi_auto')}
                    className={`px-3 py-1.5 transition-colors ${liAutonomy === 'semi_auto' ? 'bg-sky-500/15 text-sky-500 font-medium' : 'text-muted-foreground hover:bg-muted'} ${liEnabled ? '' : 'cursor-not-allowed'}`}
                    data-testid="linkedin-autonomy-draft"
                  >
                    Draft for approval
                  </button>
                  <button
                    type="button"
                    disabled={!liEnabled}
                    onClick={() => setLinkedinAutonomy('full_auto')}
                    className={`px-3 py-1.5 transition-colors border-l border-border/60 ${liAutonomy === 'full_auto' ? 'bg-emerald-500/15 text-emerald-500 font-medium' : 'text-muted-foreground hover:bg-muted'} ${liEnabled ? '' : 'cursor-not-allowed'}`}
                    data-testid="linkedin-autonomy-auto-send"
                  >
                    Auto-send
                  </button>
                </div>
              </div>
            </div>
          )}
        </React.Fragment>
      ))}

      {/* Pricing policy — same rule applied on email & WhatsApp */}
      <div className="flex items-center justify-between gap-4 px-4 py-2.5" data-testid="autonomy-row-pricing_reply_policy">
        <div className="flex items-start gap-3">
          <Tag className="h-4 w-4 mt-0.5 text-amber-500" />
          <div>
            <p className="text-sm font-medium text-foreground">Pricing questions</p>
            <p className="text-xs text-muted-foreground">How the Brain handles a price ask on email & WhatsApp.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {savingKey === 'pricing_reply_policy' ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
          <div className="inline-flex rounded-lg border border-border/60 overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setPricingPolicy('escalate')}
              className={`px-3 py-1.5 transition-colors ${pricingPolicy === 'escalate' ? 'bg-amber-500/15 text-amber-500 font-medium' : 'text-muted-foreground hover:bg-muted'}`}
              data-testid="pricing-policy-escalate"
            >
              Escalate
            </button>
            <button
              type="button"
              onClick={() => setPricingPolicy('auto_answer')}
              className={`px-3 py-1.5 transition-colors border-l border-border/60 ${pricingPolicy === 'auto_answer' ? 'bg-emerald-500/15 text-emerald-500 font-medium' : 'text-muted-foreground hover:bg-muted'}`}
              data-testid="pricing-policy-auto-answer"
            >
              Auto-answer
            </button>
          </div>
        </div>
      </div>

      {/* Ops routing — hand the lead to Operations once every required doc arrives */}
      <div className="flex items-center justify-between gap-4 px-4 py-2.5" data-testid="autonomy-row-ops_team_assignee">
        <div className="flex items-start gap-3">
          <UserCog className="h-4 w-4 mt-0.5 text-blue-500" />
          <div>
            <p className="text-sm font-medium text-foreground">Forward to Operations on docs-complete</p>
            <p className="text-xs text-muted-foreground">When every required document is received, auto-assign the lead to this teammate.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {savingKey === 'ops_team_assignee' ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
          <select
            value={settings.ops_team_assignee || ''}
            onChange={(e) => setOpsAssignee(e.target.value)}
            className="text-xs rounded-lg border border-border/60 bg-background px-2 py-1.5 max-w-[180px]"
            data-testid="ops-assignee-select"
          >
            <option value="">Not set</option>
            {teamMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {(m.name || m.email || m.id)}{m.role ? ` — ${m.role}` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default AutonomyControls;
